import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import useScrollBack from '@stepflow/ui-shared/hooks/useScrollBack.js';
import useStickToBottom from '@stepflow/ui-shared/hooks/useStickToBottom.js';
import { getThread } from '@stepflow/ui-shared/services/dataPlaneApi';
import { mergeDiscussion, withContinuations } from '../../utils/contextDiscussion.js';
import { MSG_TEXT_CLASS } from './ContextDiscussion.jsx';

/** A screenful of replies per page — same Slack-style scroll-back as the room stream. */
export const THREAD_PAGE = 50;

/** A thread shows ONLY replies to ITS root — never the root again, never a room message (even if a server answers
 *  with the room's page, e.g. an older backend that ignores ``threadOf``). Pure. */
export function onlyRepliesOf(rootId, messages = []) {
  return (messages || []).filter((m) => m && m.threadOf === rootId && m.id !== rootId);
}

/** EXPERTS AS TOOLS (EXPERT-ROOMS §19) — the root is an agent's question to an expert and the expert has since
 *  answered in this thread → ``{agentId, expertName, answer}`` for "Continue with its answer", else null. Pure. */
export function expertContinuation(root, replies = []) {
  const ask = root && root.expertAsk;
  if (!ask || !ask.agentId || !ask.expertNetworkId) return null;
  const mine = (replies || []).filter((m) => m && !m.deleted
    && (m.agentIds || []).includes(`expert:${ask.expertNetworkId}`) && String(m.text || '').trim());
  const last = mine[mine.length - 1];
  return last ? { agentId: ask.agentId, expertName: ask.expertName || 'the expert', answer: String(last.text) } : null;
}

/**
 * THE THREAD PANE (MODES.md §3b — Slack's right-hand thread view). Its OWN column beside the room stream (the network
 * diagram stays visible next to it): header "Thread" + ✕; the ROOT message; a "N replies" divider; the replies
 * (full messages — signals, edit, delete, copy — but no "Reply in thread": ONE level); a reply box pinned at the
 * bottom (Enter sends · Shift+Enter new line · Esc closes). Loads ONLY this root's replies, newest page first, older
 * on scroll-up; live replies / edits arrive through ``liveReplies`` (the room's channel) and merge by revision.
 */
export default function ThreadPane({ networkId, poolId, root, liveReplies = [], focus = false, onClose, onSend,
  renderMessage, roomLabel = '', me = '', onContinue = null }) {
  const [replies, setReplies] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const box = useRef(null);
  const rootId = root && root.id;

  useEffect(() => {
    if (!networkId || !rootId) return undefined;
    let cancelled = false;
    setReplies([]); setHasMore(false); setLoading(true); setError('');
    getThread(networkId, root, { pool: poolId, limit: THREAD_PAGE })
      .then((res) => {
        if (cancelled) return;
        if (!res || !res.root) {
          // not a thread answer (a backend without the thread route returns the ROOM's page) → show nothing wrong
          setError('Threads need the latest server — this one returned the room instead.');
          return;
        }
        setReplies(mergeDiscussion([], onlyRepliesOf(rootId, res.messages)));
        setHasMore(!!res.hasMore);
      })
      .catch(() => { if (!cancelled) setError("Couldn't load the thread."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [networkId, poolId, rootId]); // eslint-disable-line react-hooks/exhaustive-deps

  // live: a new reply / an edit / a signal on a reply of THIS thread (the room's channel carries it)
  useEffect(() => {
    const mine = onlyRepliesOf(rootId, liveReplies);
    if (mine.length) setReplies((r) => mergeDiscussion(r, mine));
  }, [liveReplies, rootId]);

  useEffect(() => { if (focus && box.current) box.current.focus(); }, [focus, rootId]);

  const loadOlder = useCallback(async () => {
    const oldest = replies[0];
    if (!oldest || !oldest.sk) { setHasMore(false); return; }
    const res = await getThread(networkId, root, { pool: poolId, limit: THREAD_PAGE, before: oldest.sk });
    setReplies((r) => mergeDiscussion(r, onlyRepliesOf(rootId, res?.messages)));
    setHasMore(!!res?.hasMore && (res?.messages || []).length > 0);
  }, [replies, networkId, poolId, root]);
  const { sentinelRef } = useScrollBack({ rootRef: scrollRef, hasMore, onLoadOlder: loadOlder, firstKey: (replies[0] || {}).id || '' });
  const shown = useMemo(() => replies.filter((m) => m && !m.deleted), [replies]);
  const stick = useStickToBottom({ rootRef: scrollRef, items: shown, resetKey: rootId || '',
    isMine: () => true });   // in a thread every arrival keeps you at the newest (Slack)

  // open at the newest reply
  const _opened = useRef('');
  useEffect(() => {
    if (loading || _opened.current === rootId) return;
    _opened.current = rootId;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [loading, rootId]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setError('');
    try {
      const res = await onSend(root, t);
      if (res && res.message) setReplies((r) => mergeDiscussion(r, onlyRepliesOf(rootId, [res.message])));
      setText('');
    } catch (e) {
      setError((e && e.message) || "Couldn't send — try again.");
    }
    setBusy(false);
  };

  if (!root) return null;
  const count = Number(root.replyCount) || shown.length;
  const cont = onContinue ? expertContinuation(root, shown) : null;
  return (
    <aside data-testid="thread-pane" role="dialog" aria-label="Thread"
      className="hidden w-[24rem] shrink-0 flex-col border-l border-gray-200 bg-white md:flex">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-200 px-4">
        <div className="min-w-0 flex-1">
          <div className="text-[0.9375rem] font-bold text-gray-900">Thread</div>
          {roomLabel ? <div className="truncate text-[0.75rem] text-gray-500">{roomLabel}</div> : null}
        </div>
        <button type="button" data-testid="thread-pane-close" aria-label="Close thread" title="Close"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900">
          <X className="h-[1.125rem] w-[1.125rem]" aria-hidden />
        </button>
      </div>
      <div ref={scrollRef} data-testid="thread-pane-scroll" className="sf-room-surface min-h-0 flex-1 overflow-y-auto px-4 pb-2 pt-3">
        {renderMessage(root, { isRoot: true })}
        {count ? (
          <div data-testid="thread-pane-divider" className="my-2 flex items-center gap-2 text-[0.75rem] text-gray-500">
            <span>{count} {count === 1 ? 'reply' : 'replies'}</span>
            <span className="h-px flex-1 bg-gray-200" aria-hidden />
          </div>
        ) : null}
        <div ref={sentinelRef} aria-hidden />
        {hasMore ? (
          <div className="flex justify-center py-1 text-[11px] text-gray-400">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> Loading earlier replies…
          </div>
        ) : null}
        {loading ? (
          <div className="flex justify-center py-4 text-[0.75rem] text-gray-400" data-testid="thread-pane-loading">
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden /> Loading…
          </div>
        ) : null}
        {!loading && error ? (
          <div data-testid="thread-pane-error" className="py-3 text-center text-[0.8125rem] text-rose-600">{error}</div>
        ) : null}
        {/* SLACK DENSITY (MODES.md §3f): no gap; consecutive replies by the same author group */}
        <div>{withContinuations(shown.map((m) => ({ kind: 'message', item: m })), me)
          .map((it) => renderMessage(it.item, { isRoot: false, compact: !!it.compact }))}</div>
        {stick.newCount > 0 ? (
          <div className="pointer-events-none sticky bottom-1 flex justify-center">
            <button type="button" onClick={stick.jumpToBottom}
              className="pointer-events-auto rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-medium text-white shadow-md">
              {stick.newCount} new
            </button>
          </div>
        ) : null}
      </div>
      <div className="shrink-0 px-4 pb-3 pt-1">
        {cont ? (
          <button type="button" data-testid="thread-expert-continue" disabled={busy}
            onClick={async () => { setBusy(true); try { await onContinue(root, cont); } catch (e) { setError((e && e.message) || "Couldn't continue."); } setBusy(false); }}
            className="mb-2 w-full px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40">
            Continue with {cont.expertName}&apos;s answer
          </button>
        ) : null}
        <div className="rounded-lg border border-gray-300 bg-white p-2 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-300">
          <textarea ref={box} data-testid="thread-pane-input" value={text} rows={2} disabled={busy} placeholder="Reply…"
            aria-label="Reply to thread" onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); onClose(); }
              else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
            }}
            className={`w-full resize-none border-0 p-0 ${MSG_TEXT_CLASS} text-gray-900 outline-none focus:ring-0`} />
          <div className="flex items-center justify-between text-[0.75rem] text-gray-400">
            <span>{error ? <span className="text-rose-600">{error}</span> : 'Enter to send · Shift+Enter for a new line'}</span>
            <button type="button" data-testid="thread-pane-send" onClick={() => void send()} disabled={busy || !text.trim()}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
              Send
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
